import ThemeBackground from "@pathwright/ui/src/components/background/ThemeBackground"
import { getUrlFromRoute } from "@pathwright/ui/src/components/link/utils"
import { PreviousLocationProvider } from "@pathwright/features/src/modules/utils/usePreviousLocation"
import React, { Suspense, useEffect, useMemo, useState } from "react"
import { createRoot } from "react-dom/client"
import lazy from "react-lazy-with-preload"
import {
  BrowserRouter,
  Redirect,
  Route,
  Switch,
  generatePath,
  useHistory,
  useLocation,
  useRouteMatch
} from "react-router-dom"
import { QueryParamProvider } from "use-query-params"
import App from "./App"
import ClientRoutes from "./ClientRoutes"
import { bootApp } from "./boot"
import EmbedsRoute from "./embeds/routes"
import {
  BackgroundPage,
  BackgroundPageContext,
  RouteLayer,
  createBackgroundPageStore
} from "./lib/core/routing/AppRoute"
import InvalidRoute from "./lib/core/routing/InvalidRoute"
import { OverlaySlotsProvider } from "./lib/core/routing/OverlaySlots"
import {
  classifyLocation,
  getLocationUrl,
  installBackgroundHistory,
  useBackgroundLocation,
  useRouteRegistryVersion
} from "./lib/core/routing/routeRegistry"
import { HistoryListener } from "./lib/core/routing/router"
import {
  ROUTE_TABLE_PRIORITY,
  createRoutes,
  getInitialRedirect,
  historyListener
} from "./lib/core/routing/utils"
import SignupRoute, {
  LegacySignupRedirect
} from "./signup/routes"

// pathItemSourceId: [source_id, step_id]
// pathItemId: [completion_id]
// cohortId: [offering, cohort_id, group_id]
// resourceSlug: [slug]
// resourceId: [resource_id]
// discussionId: [discussion_id]
// responseId: [response_id]
// authorId: [author_id]
// categoryId: [category_id]
// bannerId: [banner_id]
// subscriptionId: [subscription_id]
// communityArea: [community_area]
// pageId: [page_id]

const LazyLibraryRoutes = lazy(() =>
  import("library/routes" /* webpackChunkName: "LibraryRoutes" */)
)

// HACK ALERT: allowing CompletionPreview to preload library/routes
// in order to assign AssessmentSubmissionPreview to window.
window.LazyLibraryRoutes = LazyLibraryRoutes

const LazyStoreRoutes = React.lazy(() =>
  import("store/routes" /* webpackChunkName: "StoreRoutes" */)
)

const getAppRoutes = () =>
  createRoutes(
    [
      require("school/routes").pageRoute,
      require("school/routes").pageModalRoute,
      require("school/routes").schoolInactiveRoute,
      require("school/routes").sitemapRoute,
      require("school/routes").faqsRoute,
      require("auth/global/routes").default,
      require("unified-home/routes").default,
      require("home/routes").createCohortCardRoute,
      require("home/routes").default,
      require("mentor-group/routes").default,
      require("dashboard/routes").default,
      require("join/routes").default,
      require("completion/routes").default,
      require("billing/display/routes").default,
      require("billing/display/routes").upgradeCardRoutes,
      require("billing/confirm-stripe-routes").default,
      require("sso/routes").default,
      require("order/routes").subscriptionRoute,
      require("order/routes").giftSubscriptionRoute,
      require("order/routes").curriculumLicenseeLicenseRoute,
      require("order/routes").curriculumSubscriptionRoute,

      // Modals
      require("library/cover-page/routes").libraryCoverPageRoutes,
      require("library/cover-page/routes").default,
      require("library/browse/routes").default,
      require("profile/routes").default,
      require("calendar/routes").default,
      require("certificate/routes").default,
      require("inbox/routes").default,
      require("invitation/invite/routes").schoolRoute,
      require("invitation/list/routes").schoolRoute,
      require("home/components/manage/routes").default,
      require("home/components/manage/routes").billingCardRoutes,
      require("resource/components/manage/routes").default,
      require("group/components/manage/routes").default,
      require("support/routes").default,
      require("category/routes").default,
      require("author/routes").default,
      require("banner/routes").default,
      // require("account/subscription/routes").default,
      // require("account/subscription/gift/routes").default,
      require("school/manage/routes").default,
      require("account/subscription/gift/redeem-routes").default
    ].filter(Boolean),
    { key: "app", priority: ROUTE_TABLE_PRIORITY.app }
  )

// prettier-ignore
// These are routes that were moved.
// We could possible add some Sentry logging to see if these routes are still being hit.
const redirects = {
  '/manage/school/brand/': '/manage/school/account/brand/',
  '/manage/school/commerce/': '/manage/school/features/commerce/',
  '/manage/school/integrations/': '/manage/school/features/integrations/',
  '/manage/school/features/custom-sso/': '/manage/school/features/auth/custom-sso/',
  // '/manage/school/features/:featureKey/': '/manage/school/features/auth/:featureKey/',
  '/manage/resource/:resourceId(\\d+)/group/': '/manage/resource/:resourceId(\\d+)/cohorts/',
  '/manage/school/features/discussion-topics/': '/manage/school/features/cohort/discussion-topics/',
  '/manage/school/features/pathwright-signin/': '/manage/school/features/auth/pathwright-signin/',
  '/manage/resource/:resource_id(\\d+)/:group_id(\\d+)/': '/manage/resource/:resource_id(\\d+)/cohorts/:group_id(\\d+)/',
  '/manage/resource/:resource_id(\\d+)/advanced/archive/': '/manage/resource/:resource_id(\\d+)/archive/',
  '/manage/resource/:resource_id(\\d+)/advanced/license/': '/manage/resource/:resource_id(\\d+)/cohorts/license/',
  '/manage/resource/:resource_id(\\d+)/advanced/license/:resource_license_offering_id(\\d+)/': '/manage/resource/:resource_id(\\d+)/cohorts/license/:resource_license_offering_id(\\d+)/'
}

let appRoutes = null
const getAppRoutesOnce = () => (appRoutes ||= getAppRoutes())

function AuthRoutes({ authSync }) {
  // Wrapping with an /auth/ path matcher to because
  // 1) we are only concerned with /auth/ routes and
  // 2) we only want to render the ThemeBackground here
  // when showing auth components. This is basically an escape
  // hatch for rendering a consistent theme bg as we're getting
  // slightly different results when using the space.theme's bg.
  return (
    <Route
      path="/auth/"
      render={() => (
        <>
          {/* NOTE: "fixed" position necessary due to layout (overrides "absolute") */}
          <ThemeBackground style={{ position: "fixed" }} />
          <ClientRoutes authSync={authSync} />
        </>
      )}
    />
  )
}

// Pages for the background location. Redirects, auth, and the invalid route
// only render in the foreground.
function BackgroundRoutes({ location }) {
  const [pageStore] = useState(createBackgroundPageStore)

  return (
    <BackgroundPageContext.Provider value={pageStore}>
      <Suspense fallback={null}>
        <Switch location={location}>
          {getAppRoutesOnce()}
          <Route path="/library/">
            <LazyLibraryRoutes />
          </Route>
          <Route path="/store/">
            <LazyStoreRoutes />
          </Route>
        </Switch>
      </Suspense>
      <Suspense fallback={null}>
        <BackgroundPage />
      </Suspense>
    </BackgroundPageContext.Provider>
  )
}

// NOTE: using class component due to some odd warnings related to hooks
// when using a functional component.
class RootRoute extends React.Component {
  constructor(props) {
    super(props)
    this.appRoutes = getAppRoutesOnce()
  }

  render() {
    return (
      <Route
        render={(props) => {
          const rootRedirect = getInitialRedirect(props.location)
          return rootRedirect &&
            // Ensurce we are not already at said rootRedirect
            getUrlFromRoute(rootRedirect) !==
              props.location.pathname +
                props.location.search +
                props.location.hash ? (
            <Redirect to={rootRedirect} />
          ) : (
            <>
              <Switch>
                {Object.entries(redirects).map(([path, redirect]) => (
                  <Route
                    key={path}
                    path={path}
                    render={({ match }) => (
                      <Redirect to={generatePath(redirect, match.params)} />
                    )}
                    exact
                  />
                ))}
                {this.appRoutes}
                <Route path="/library/">
                  <LazyLibraryRoutes />
                </Route>
                <Route path="/store/">
                  <LazyStoreRoutes />
                </Route>
                <AuthRoutes authSync={this.props.authSync} />
                <InvalidRoute />
              </Switch>
              {/* Always attempt to match the card redirect. */}
              {/* NOTE: to entirely prevent ?AUGMENTATION_PANEL_QUERY_PARAM routing, remove CardMatchRedirect */}
              {/* <CardMatchRedirect routes={this.appRoutes} /> */}
            </>
          )
        }}
      />
    )
  }
}

// Sync the legacy navigation store with the current location and whether an
// overlay is open over a background.
function useNavigationStoreSync(location, backgroundLocation) {
  const registryVersion = useRouteRegistryVersion()
  const url = getLocationUrl(location)

  useEffect(() => {
    historyListener(location)
  }, [url])

  const classification = useMemo(
    () => classifyLocation(location.pathname),
    [location.pathname, registryVersion]
  )
  const isOverlay = classification?.kind === "overlay"
  const components = classification?.route.components
  // The inbox (a secondary component) isn't treated as a modal.
  const isSecondary =
    isOverlay &&
    !!components?.secondary &&
    !components.modal &&
    !components.chakraModal &&
    !classification.route.cards?.length
  const launchedFrom =
    isOverlay && backgroundLocation ? getLocationUrl(backgroundLocation) : null

  // Runs after historyListener, whose setLocation resets is_showing_modal.
  useEffect(() => {
    window.App.getStore("navigation").action.setIsShowingModal(
      isOverlay && !isSecondary,
      launchedFrom
    )
  }, [url, isOverlay, isSecondary, launchedFrom])
}

function BootedApp() {
  const location = useLocation()
  const backgroundLocation = useBackgroundLocation(location)
  // Auth routes render no background, which would otherwise keep the previous
  // route's components (and their queries) mounted behind the auth UI while
  // the auth token changes.
  const isAuthRoute = !!useRouteMatch("/auth/")

  useNavigationStoreSync(location, backgroundLocation)

  const primary =
    backgroundLocation && !isAuthRoute ? (
      <RouteLayer
        layer="background"
        location={location}
        backgroundLocation={backgroundLocation}
      >
        <BackgroundRoutes location={backgroundLocation} />
      </RouteLayer>
    ) : null

  return (
    <OverlaySlotsProvider>
      <App primary={primary}>
        {({ authSync }) => (
          <RouteLayer
            layer="foreground"
            location={location}
            backgroundLocation={backgroundLocation}
          >
            <Suspense fallback={null}>
              {/* For now, show nothing when waiting on lazy components to load.
              This prevents showing somethign like a LoadingCircle overtop unrelated UIs. */}
              <RootRoute authSync={authSync} />
            </Suspense>
          </RouteLayer>
        )}
      </App>
    </OverlaySlotsProvider>
  )
}

function AppBooter() {
  const [hasBooted, setHasBooted] = useState(false)

  // At this point we haven't yet booted up the legacy Backbone App
  // so let's do that now.
  useEffect(() => {
    bootApp().then(() => {
      // Building the routes evaluates their modules, which must happen outside
      // of a render, and registers them before the background is resolved.
      getAppRoutesOnce()
      setHasBooted(true)
    })
  }, [])

  return hasBooted ? <BootedApp /> : null
}

// Stamp each history entry with the background it opens over, before the
// entry is written, so both route layers render from the same location.
function BackgroundHistory({ children }) {
  const history = useHistory()
  useState(() => installBackgroundHistory(history))
  return children
}

const app = (
  <BrowserRouter>
    <BackgroundHistory>
      <QueryParamProvider ReactRouterRoute={Route}>
        <PreviousLocationProvider>
          <HistoryListener />
          <Switch>
          {/* Hoist any routes that can be independent of the app here. */}
          <Route path={EmbedsRoute.path} render={EmbedsRoute} />
          <Route
            path={LegacySignupRedirect.path}
            render={LegacySignupRedirect}
          />
          <Route path={SignupRoute.path} render={SignupRoute} exact />
          <Route path="*" render={() => <AppBooter />} />
          </Switch>
        </PreviousLocationProvider>
      </QueryParamProvider>
    </BackgroundHistory>
  </BrowserRouter>
)

const root = createRoot(document.getElementById("root"))

root.render(app)
